'use client';

interface Props {
  label: string;
  value: number;
  originalValue: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  formatValue: (v: number) => string;
}

export function Slider({ label, value, originalValue, min, max, step, onChange, formatValue }: Props) {
  const isModified = value !== originalValue;
  const range = max - min || 1;
  const pctOfRange = ((value - min) / range) * 100;
  const originalPct = ((originalValue - min) / range) * 100;

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-xs font-medium text-text-primary">{label}</label>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold font-mono tabular-nums ${isModified ? 'text-success' : 'text-text-primary'}`}>
            {formatValue(value)}
          </span>
          {isModified && (
            <span className="font-mono text-2xs text-text-tertiary">
              de <span className="line-through">{formatValue(originalValue)}</span>
            </span>
          )}
        </div>
      </div>
      <div className="relative flex h-5 items-center">
        <div className="absolute inset-x-0 h-1 rounded-full bg-muted" />
        <div
          className="absolute h-1 rounded-full"
          style={{
            left: 0,
            width: `${pctOfRange}%`,
            backgroundColor: isModified ? '#059669' : '#94a3b8',
            transition: 'width 80ms',
          }}
        />
        {isModified && originalPct >= 0 && originalPct <= 100 && (
          <div
            className="absolute h-3 w-0.5 bg-neutral-400/60"
            style={{ left: `calc(${originalPct}% - 1px)` }}
            title={`original: ${formatValue(originalValue)}`}
          />
        )}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full cursor-pointer opacity-0"
        />
        <div
          className="pointer-events-none absolute size-4 rounded-full"
          style={{
            left: `calc(${pctOfRange}% - 8px)`,
            backgroundColor: isModified ? '#059669' : '#ffffff',
            border: '2px solid ' + (isModified ? '#047857' : '#94a3b8'),
            boxShadow: '0 1px 3px rgba(15,23,42,0.15)',
            transition: 'left 80ms',
          }}
        />
      </div>
      <div className="mt-1 flex items-center justify-between font-mono text-2xs text-text-tertiary">
        <span>{formatValue(min)}</span>
        <span>{formatValue(max)}</span>
      </div>
    </div>
  );
}
